<template>
  <span>
    <!-- Botón disparador (opcional, para reutilizar el modal desde otras vistas) -->
    <button
      v-if="show_button"
      type="button"
      :class="`btn btn-sm btn-ai-quick ${button_class}`"
      @click="open"
    >
      <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
      {{ $t("reusable.aiCurricularBuilderModal.openButton") }}
    </button>

    <!-- Modal (BS4 manual, sin Bootstrap-Vue) -->
    <div v-if="visible" class="ai-builder-overlay">
      <div class="ai-builder-dialog" role="dialog" aria-modal="true">
        <div class="modal-content ai-builder-content">
          <div class="modal-header py-2">
            <h5 class="modal-title">
              <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
              {{ $t("reusable.aiCurricularBuilderModal.title") }}
            </h5>
            <button
              type="button"
              class="close"
              :aria-label="$t('reusable.aiCurricularBuilderModal.close')"
              @click="close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body ai-builder-body">
            <!-- Error -->
            <div v-if="error" class="alert alert-danger py-2">
              {{ error }}
            </div>

            <!-- Cargando contexto -->
            <div v-if="loading" class="text-center my-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted">
                {{ $t("reusable.aiCurricularBuilderModal.generating") }}
              </p>
            </div>

            <template v-else>
              <!-- Indicador de generación por streaming -->
              <div
                v-if="generating"
                class="ai-builder-generating d-flex align-items-center mb-3"
              >
                <div
                  class="spinner-border spinner-border-sm mr-2"
                  role="status"
                ></div>
                <span>{{
                  $t("reusable.aiCurricularBuilderModal.generating")
                }}</span>
              </div>

              <p
                v-if="!generating && unit_groups.length == 0"
                class="text-muted small"
              >
                {{ $t("reusable.aiCurricularBuilderModal.empty") }}
              </p>

              <!-- Cascada: unidad de competencia → RA → indicadores/contenidos -->
              <div v-for="group in unit_groups" :key="`group-${group.key}`">
                <AiBuilderItemCard
                  v-if="group.unit"
                  :item="group.unit"
                  :busy="busy"
                  collapsible
                  :collapsed="isCollapsed(group.unit.key)"
                  @toggle-collapse="toggleCollapsed(group.unit.key)"
                  @remove="removeUnit(group.unit)"
                  @restore="restoreUnit(group.unit)"
                  @regenerate="regenerateUnit(group.unit)"
                >
                  <template #header>
                    <span class="badge badge-info mr-1">{{ unit_label }}</span>
                    <strong>{{ group.unit.name }}</strong>
                  </template>
                  <template #body>
                    <p class="mb-1 mt-1 ai-builder-description">
                      {{ group.unit.full_sentence }}
                    </p>
                  </template>
                </AiBuilderItemCard>

                <div
                  v-show="group.unit == null || !isCollapsed(group.unit.key)"
                  :class="{ 'ai-builder-nested': group.unit != null }"
                >
                  <template v-for="study_unit in group.study_units">
                    <AiBuilderItemCard
                      :key="study_unit.key"
                      :item="study_unit"
                      :busy="busy"
                      collapsible
                      :collapsed="isCollapsed(study_unit.key)"
                      @toggle-collapse="toggleCollapsed(study_unit.key)"
                      @remove="removeStudyUnit(study_unit)"
                      @restore="restoreStudyUnit(study_unit)"
                      @regenerate="regenerateStudyUnit(study_unit)"
                    >
                      <template #header>
                        <span class="badge badge-primary mr-1">{{
                          study_unit_label
                        }}</span>
                        <strong>{{ study_unit.name }}</strong>
                      </template>
                      <template #body>
                        <p class="mb-1 mt-1 ai-builder-description">
                          {{ study_unit.full_sentence }}
                        </p>
                      </template>
                    </AiBuilderItemCard>

                    <div
                      v-show="!isCollapsed(study_unit.key)"
                      :key="`children-${study_unit.key}`"
                      class="ai-builder-nested"
                    >
                      <template
                        v-if="
                          criteriaOf(study_unit.key).length > 0 ||
                          !study_unit.deleted
                        "
                      >
                        <h6 class="ai-builder-section-title">
                          {{ criteria_title }}
                        </h6>
                        <AiBuilderItemCard
                          v-for="criterion in criteriaOf(study_unit.key)"
                          :key="criterion.key"
                          :item="criterion"
                          :busy="busy"
                          @remove="criterion.deleted = true"
                          @restore="criterion.deleted = false"
                          @regenerate="regenerateCriteria(criterion)"
                        >
                          <template #body>
                            <p class="mb-1 mt-1 ai-builder-description">
                              {{ criterion.full_sentence }}
                            </p>
                          </template>
                        </AiBuilderItemCard>

                        <!-- Agregar un indicador de logro NUEVO a este RA -->
                        <AiThemeInput
                          v-if="!study_unit.deleted"
                          class="mt-1 mb-3"
                          :value="add_criteria_themes[study_unit.key] || ''"
                          :placeholder="
                            $t(
                              'reusable.aiCurricularBuilderModal.addCriteriaThemePlaceholder',
                              { entity: criterion_label }
                            )
                          "
                          :hint="
                            $t(
                              'reusable.aiCurricularBuilderModal.addCriteriaThemeHint',
                              {
                                entity: criterion_label,
                                study_unit: study_unit_label,
                              }
                            )
                          "
                          :info="
                            $t(
                              'reusable.aiCurricularBuilderModal.addCriteriaThemeInfo',
                              { entity: criterion_label }
                            )
                          "
                          :disabled="busy"
                          @input="
                            setAddCriteriaTheme(study_unit.key, $event)
                          "
                        >
                          <template #append>
                            <button
                              type="button"
                              class="btn ai-builder-btn-regen"
                              :disabled="busy || loading"
                              @click="addCriteria(study_unit)"
                            >
                              <span aria-hidden="true" class="mr-1">+</span
                              >{{
                                $t(
                                  "reusable.aiCurricularBuilderModal.addCriteriaButton",
                                  { entity: criterion_label }
                                )
                              }}
                            </button>
                          </template>
                        </AiThemeInput>
                      </template>

                      <template
                        v-if="
                          show_contents &&
                          (contentsOf(study_unit.key).length > 0 ||
                            !study_unit.deleted)
                        "
                      >
                        <h6 class="ai-builder-section-title">
                          {{ contents_title }}
                        </h6>
                        <AiBuilderItemCard
                          v-for="content in contentsOf(study_unit.key)"
                          :key="content.key"
                          :item="content"
                          :busy="busy"
                          @remove="content.deleted = true"
                          @restore="content.deleted = false"
                          @regenerate="regenerateContent(content)"
                        >
                          <template #body>
                            <p class="mb-1 mt-1 ai-builder-description">
                              {{ content.content }}
                            </p>
                          </template>
                        </AiBuilderItemCard>

                        <!-- Agregar un contenido NUEVO a este RA -->
                        <AiThemeInput
                          v-if="!study_unit.deleted"
                          class="mt-1 mb-3"
                          :value="add_content_themes[study_unit.key] || ''"
                          :placeholder="
                            $t(
                              'reusable.aiCurricularBuilderModal.addContentThemePlaceholder',
                              { entity: content_label }
                            )
                          "
                          :hint="
                            $t(
                              'reusable.aiCurricularBuilderModal.addContentThemeHint',
                              {
                                entity: content_label,
                                study_unit: study_unit_label,
                              }
                            )
                          "
                          :info="
                            $t(
                              'reusable.aiCurricularBuilderModal.addContentThemeInfo',
                              { entity: content_label }
                            )
                          "
                          :disabled="busy"
                          @input="setAddContentTheme(study_unit.key, $event)"
                        >
                          <template #append>
                            <button
                              type="button"
                              class="btn ai-builder-btn-regen"
                              :disabled="busy || loading"
                              @click="addContent(study_unit)"
                            >
                              <span aria-hidden="true" class="mr-1">+</span
                              >{{
                                $t(
                                  "reusable.aiCurricularBuilderModal.addContentButton",
                                  { entity: content_label }
                                )
                              }}
                            </button>
                          </template>
                        </AiThemeInput>
                      </template>
                    </div>
                  </template>

                  <!-- Agregar un RA NUEVO (con indicadores + contenidos)
                       colgando de esta unidad de competencia -->
                  <AiThemeInput
                    v-if="group.unit && !group.unit.deleted"
                    class="mt-1 mb-3"
                    :value="add_study_unit_themes[group.key] || ''"
                    :placeholder="
                      $t(
                        'reusable.aiCurricularBuilderModal.addStudyUnitThemePlaceholder',
                        { entity: study_unit_label }
                      )
                    "
                    :hint="
                      $t(
                        `reusable.aiCurricularBuilderModal.addStudyUnitThemeHint${no_contents_suffix}`,
                        {
                          entity: study_unit_label,
                          criteria: criteria_title,
                          contents: contents_title,
                        }
                      )
                    "
                    :info="
                      $t(
                        `reusable.aiCurricularBuilderModal.addStudyUnitThemeInfo${no_contents_suffix}`,
                        { criteria: criteria_title, contents: contents_title }
                      )
                    "
                    :disabled="busy"
                    @input="setAddStudyUnitTheme(group.key, $event)"
                  >
                    <template #append>
                      <button
                        type="button"
                        class="btn ai-builder-btn-regen"
                        :disabled="busy || loading"
                        @click="addStudyUnitTree(group.unit)"
                      >
                        <span aria-hidden="true" class="mr-1">+</span
                        >{{
                          $t(
                            "reusable.aiCurricularBuilderModal.addStudyUnitButton",
                            { entity: study_unit_label }
                          )
                        }}
                      </button>
                    </template>
                  </AiThemeInput>
                </div>
              </div>

              <!-- Agregar una unidad de competencia NUEVA con su subárbol
                   (RAs + indicadores + contenidos), incluso si la
                   asignatura ya tiene estructura -->
              <AiThemeInput
                v-if="can_add_unit"
                v-model="add_unit_theme"
                class="mt-2"
                :placeholder="
                  $t(
                    'reusable.aiCurricularBuilderModal.addUnitThemePlaceholder',
                    { entity: unit_label }
                  )
                "
                :hint="
                  $t(`reusable.aiCurricularBuilderModal.addUnitThemeHint${no_contents_suffix}`, {
                    entity: unit_label,
                    study_units: study_units_label,
                    criteria: criteria_title,
                    contents: contents_title,
                  })
                "
                :info="
                  $t(`reusable.aiCurricularBuilderModal.addUnitThemeInfo${no_contents_suffix}`, {
                    entity: unit_label,
                    study_units: study_units_label,
                    criteria: criteria_title,
                    contents: contents_title,
                  })
                "
                :disabled="busy"
              >
                <template #append>
                  <button
                    type="button"
                    class="btn ai-builder-btn-regen"
                    :disabled="busy || loading"
                    @click="addUnitTree"
                  >
                    <span aria-hidden="true" class="mr-1">+</span
                    >{{
                      $t("reusable.aiCurricularBuilderModal.addUnitButton", {
                        entity: unit_label,
                      })
                    }}
                  </button>
                </template>
              </AiThemeInput>

              <!-- Institución sin unidades de competencia: se construye con
                   una capa menos, RA por RA (cada uno con sus indicadores y
                   contenidos), directo bajo la asignatura -->
              <AiThemeInput
                v-if="!can_add_unit"
                class="mt-2"
                :value="add_study_unit_themes['__none__'] || ''"
                :placeholder="
                  $t(
                    'reusable.aiCurricularBuilderModal.addStudyUnitThemePlaceholder',
                    { entity: study_unit_label }
                  )
                "
                :hint="
                  $t(
                    `reusable.aiCurricularBuilderModal.addStudyUnitThemeHint${no_contents_suffix}`,
                    {
                      entity: study_unit_label,
                      criteria: criteria_title,
                      contents: contents_title,
                    }
                  )
                "
                :info="
                  $t(
                    `reusable.aiCurricularBuilderModal.addStudyUnitThemeInfo${no_contents_suffix}`,
                    { criteria: criteria_title, contents: contents_title }
                  )
                "
                :disabled="busy"
                @input="setAddStudyUnitTheme('__none__', $event)"
              >
                <template #append>
                  <button
                    type="button"
                    class="btn ai-builder-btn-regen"
                    :disabled="busy || loading"
                    @click="addStudyUnitTree(null)"
                  >
                    <span aria-hidden="true" class="mr-1">+</span
                    >{{
                      $t(
                        "reusable.aiCurricularBuilderModal.addStudyUnitButton",
                        { entity: study_unit_label }
                      )
                    }}
                  </button>
                </template>
              </AiThemeInput>
            </template>
          </div>

          <div class="modal-footer py-2">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              :disabled="saving"
              @click="close"
            >
              {{ $t("reusable.aiCurricularBuilderModal.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="busy || loading || !hasPendingItems"
              @click="save"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm mr-1"
                role="status"
              ></span>
              {{ $t("reusable.aiCurricularBuilderModal.confirmSave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import { ref, computed, getCurrentInstance } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import restful from "@/utils/restful";
import useAiBuilderStream from "./ai-builder/useAiBuilderStream";
import { deriveActionVerb } from "./ai-builder/sentence";
import AiBuilderItemCard from "./ai-builder/AiBuilderItemCard";
import AiThemeInput from "./ai-builder/AiThemeInput";

// Builder IA de la estructura curricular de la asignatura (cascada
// TempCompetenceUnit → StudyUnit → EvaluationCriteriaMicro +
// StudyUnitContent). La estructura vive en el Matter base (compartida entre
// planes); el EPM solo aporta el contexto del prompt. El backend responde
// con arreglos planos referenciados por clave (unit_key/study_unit_key) y
// aquí se reconstruye el árbol.
export default {
  name: "AiCurricularBuilderModal",
  components: { AiBuilderItemCard, AiThemeInput },
  props: {
    egress_profile_matter_id: {
      type: Number,
      required: true,
    },
    show_button: {
      type: Boolean,
      default: false,
    },
    button_class: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const proxy = getCurrentInstance().proxy;
    const store = proxy.$store;

    const visible = ref(false);
    const loading = ref(false);
    const generating = ref(false);
    const saving = ref(false);
    const error = ref(null);
    const matter_id = ref(null);

    // Los 4 niveles de la cascada, planos; el árbol se arma por claves
    // (los existentes usan claves "db-*", los generados las de la IA).
    const units = ref([]);
    const study_units = ref([]);
    const criteria = ref([]);
    const contents = ref([]);
    // Los indicadores/contenidos generados no traen clave propia del
    // backend: se les asigna una local para el render/reconciliación.
    let uid = 0;
    // Cada "agregar unidad"/"agregar RA" (unit_tree / study_unit_tree) es
    // un lote: sus claves de la IA ("u1", "ra1", ...) se prefijan con el
    // lote para no chocar con las de la generación inicial ni con las de
    // otros lotes.
    let batch_seq = 0;
    const add_unit_theme = ref("");
    // Temas para "agregar RA": uno por unidad de competencia (clave del
    // grupo) y "__none__" para el nivel raíz (institución sin unidades).
    // Vue 2: las claves nuevas requieren $set para ser reactivas.
    const add_study_unit_themes = ref({});
    function setAddStudyUnitTheme(group_key, value) {
      proxy.$set(add_study_unit_themes.value, group_key, value);
    }
    // Temas para "agregar indicador"/"agregar contenido": uno por RA
    // (clave del RA).
    const add_criteria_themes = ref({});
    function setAddCriteriaTheme(study_unit_key, value) {
      proxy.$set(add_criteria_themes.value, study_unit_key, value);
    }
    const add_content_themes = ref({});
    function setAddContentTheme(study_unit_key, value) {
      proxy.$set(add_content_themes.value, study_unit_key, value);
    }

    // Colapso por nodo del árbol (unidades y RAs), UI-only: se indexa por
    // clave para sobrevivir a las reconciliaciones (que preservan claves
    // pero recrean los objetos).
    const collapsed_keys = ref({});
    function isCollapsed(key) {
      return collapsed_keys.value[key] === true;
    }
    function toggleCollapsed(key) {
      proxy.$set(collapsed_keys.value, key, !isCollapsed(key));
    }

    // Nombres alternativos institucionales (mismos table_name que usa
    // MatterProgram para estos conceptos), en el idioma del usuario; el
    // fallback es la etiqueta i18n genérica.
    function visibleName(table_name, is_plural, fallback_key) {
      return proxy.$getVisibleNames(
        table_name,
        is_plural,
        proxy.$t(`reusable.aiCurricularBuilderModal.${fallback_key}`)
      );
    }
    // Computeds separados (y no un único computed-objeto) a propósito: al
    // devolver strings, cada etiqueta cachea y compara por valor de forma
    // independiente — si cambia el store de alternative_names solo se
    // actualizan los bindings cuya etiqueta realmente cambió. Un computed
    // que devuelve un objeto produce una referencia nueva en cada
    // recálculo y arrastra todos los bindings `labels.*` aunque el texto
    // sea el mismo.
    const unit_label = computed(() =>
      visibleName("teaching.tempcompetenceunit", false, "unitLabel")
    );
    const study_unit_label = computed(() =>
      visibleName("teaching.ramicro", false, "studyUnitLabel")
    );
    const study_units_label = computed(() =>
      visibleName("teaching.ramicro", true, "studyUnitsLabel")
    );
    const criterion_label = computed(() =>
      visibleName("manual.indicador_de_logro", false, "criterionLabel")
    );
    const criteria_title = computed(() =>
      visibleName("manual.indicador_de_logro", true, "criteriaTitle")
    );
    const content_label = computed(() =>
      visibleName("teaching.studyunitcontent", false, "contentLabel")
    );
    const contents_title = computed(() =>
      visibleName("teaching.studyunitcontent", true, "contentsTitle")
    );

    // Instituciones sin Contenidos por RA (p. ej. PUCV): el backend no los
    // genera y aquí no se cargan, muestran ni guardan.
    const show_contents = computed(
      () => !proxy.$hidesStudyUnitContents(store.getters.getInstitution)
    );
    // Variante de los textos de ayuda que no menciona los contenidos.
    const no_contents_suffix = computed(() =>
      show_contents.value ? "" : "NoContents"
    );

    const taxonomies = ref([]);
    const verbs = computed(() => {
      const list = [];
      taxonomies.value.forEach((t) =>
        (t.cognitive_levels || []).forEach((cl) =>
          (cl.verbs || []).forEach((v) => list.push(v))
        )
      );
      return list;
    });

    const all_items = computed(() =>
      units.value.concat(study_units.value, criteria.value, contents.value)
    );
    // El botón de agregar unidad solo aplica donde el concepto existe:
    // institución con show_competence_unit o asignatura que ya usa unidades.
    const can_add_unit = computed(() => {
      const institution = store.getters.getInstitution;
      return (
        (institution != null && institution.show_competence_unit) ||
        units.value.length > 0
      );
    });
    const busy = computed(
      () =>
        saving.value ||
        generating.value ||
        all_items.value.some((x) => x.regenerating)
    );

    function isSentenceChanged(item) {
      return item.existing && item.full_sentence !== item.original_sentence;
    }
    function isContentChanged(item) {
      return item.existing && item.content !== item.original_content;
    }
    const hasPendingItems = computed(
      () =>
        units.value
          .concat(study_units.value, criteria.value)
          .some((x) =>
            x.existing
              ? x.deleted || isSentenceChanged(x)
              : !x.deleted && x.full_sentence
          ) ||
        contents.value.some((x) =>
          x.existing
            ? x.deleted || isContentChanged(x)
            : !x.deleted && x.content
        )
    );

    // Agrupación para el render: cada unidad con sus RA; los RA sin unidad
    // (o de institución sin show_competence_unit) van en un grupo sin card
    // de unidad (mismo criterio que CompetenceUnits con tempCompetenceUnit
    // null en MatterProgram.vue).
    const unit_groups = computed(() => {
      const groups = units.value.map((unit) => ({
        key: unit.key,
        unit,
        study_units: study_units.value.filter((x) => x.unit_key === unit.key),
      }));
      const orphans = study_units.value.filter(
        (x) =>
          x.unit_key == null || !units.value.some((u) => u.key === x.unit_key)
      );
      if (orphans.length > 0)
        groups.push({ key: "__none__", unit: null, study_units: orphans });
      return groups;
    });
    function criteriaOf(study_unit_key) {
      return criteria.value.filter((x) => x.study_unit_key === study_unit_key);
    }
    function contentsOf(study_unit_key) {
      return contents.value.filter((x) => x.study_unit_key === study_unit_key);
    }

    // Las taxonomías (con sus verbos) solo se necesitan para derivar "verb"
    // al guardar: se cargan recién ahí y una sola vez.
    async function ensureTaxonomies() {
      if (taxonomies.value.length > 0) return;
      taxonomies.value = await store.dispatch(names.FETCH_TAXONOMIES);
    }

    // Conversación con el endpoint builder (SSE + manejo de 403):
    // lógica compartida con los demás builders de dominio.
    const { callBuilder } = useAiBuilderStream({
      url: `/common/curricular-builder-ai/`,
      generate_error_key: "reusable.aiCurricularBuilderModal.generateError",
    });

    function baseMarks() {
      return { existing: false, deleted: false, theme: "", regenerating: false };
    }
    function pushGeneratedUnit(item, extra = {}) {
      if (!item.key || units.value.some((x) => x.key === item.key)) return;
      units.value.push({
        key: item.key,
        name: item.name,
        full_sentence: item.full_sentence,
        ...baseMarks(),
        ...extra,
      });
    }
    function pushGeneratedStudyUnit(item, extra = {}) {
      if (!item.key || study_units.value.some((x) => x.key === item.key))
        return;
      study_units.value.push({
        key: item.key,
        unit_key: item.unit_key || null,
        name: item.name,
        full_sentence: item.full_sentence,
        ...baseMarks(),
        ...extra,
      });
    }
    function pushGeneratedCriteria(item, extra = {}) {
      if (!item.study_unit_key) return;
      criteria.value.push({
        key: `c-${++uid}`,
        study_unit_key: item.study_unit_key,
        full_sentence: item.full_sentence,
        ...baseMarks(),
        ...extra,
      });
    }
    function pushGeneratedContent(item, extra = {}) {
      if (!item.study_unit_key) return;
      contents.value.push({
        key: `t-${++uid}`,
        study_unit_key: item.study_unit_key,
        content: item.content,
        ...baseMarks(),
        ...extra,
      });
    }

    function buildExisting() {
      const matter_units = store.getters[names.COMPETENCE_UNITS].filter(
        (x) => x.matter == matter_id.value
      );
      units.value = matter_units.map((unit) => ({
        key: `db-u-${unit.id}`,
        unit_id: unit.id,
        name: unit.name,
        full_sentence: unit.full_sentence,
        original_sentence: unit.full_sentence,
        ...baseMarks(),
        existing: true,
      }));
      const matter_study_units = store.getters[names.STUDY_UNITS].filter(
        (x) => x.matter == matter_id.value
      );
      study_units.value = matter_study_units.map((study_unit) => ({
        key: `db-ra-${study_unit.id}`,
        study_unit_id: study_unit.id,
        unit_key: study_unit.temp_competence_unit
          ? `db-u-${study_unit.temp_competence_unit}`
          : null,
        name: study_unit.name,
        full_sentence: study_unit.full_sentence,
        original_sentence: study_unit.full_sentence,
        ...baseMarks(),
        existing: true,
      }));
      const study_unit_ids = matter_study_units.map((x) => x.id);
      criteria.value = store.getters[names.EVALUATION_CRITERIAS]
        .filter((x) => study_unit_ids.includes(x.study_unit))
        .map((criterion) => ({
          key: `db-c-${criterion.id}`,
          criteria_id: criterion.id,
          study_unit_key: `db-ra-${criterion.study_unit}`,
          full_sentence: criterion.full_sentence,
          original_sentence: criterion.full_sentence,
          ...baseMarks(),
          existing: true,
        }));
      if (!show_contents.value) {
        contents.value = [];
        return;
      }
      contents.value = store.getters[names.CONTENTS]
        .filter((x) => study_unit_ids.includes(x.study_unit))
        .map((content) => {
          // Los contenidos existentes pueden venir con HTML del editor de
          // texto: se muestran como texto plano.
          const text = proxy.$htmlToPlainText(content.content);
          return {
            key: `db-t-${content.id}`,
            content_id: content.id,
            study_unit_key: `db-ra-${content.study_unit}`,
            content: text,
            original_content: text,
            ...baseMarks(),
            existing: true,
          };
        });
    }

    function reconcileKeyed(list_ref, generated, makeItem) {
      // Resultado final validado del backend = autoritativo para lo
      // generado; se preservan los existentes y las marcas del usuario
      // (deleted/theme) hechas durante el streaming, casando por clave.
      const previous = list_ref.value;
      const kept = previous.filter((x) => x.existing);
      list_ref.value = kept.concat(
        generated.map((raw) => {
          const prev = previous.find((x) => !x.existing && x.key === raw.key);
          return {
            ...makeItem(raw),
            deleted: prev ? prev.deleted : false,
            theme: prev ? prev.theme : "",
          };
        })
      );
    }
    function reconcileGrouped(list_ref, generated, field) {
      // Indicadores/contenidos no traen clave propia: se casan por
      // (study_unit_key, posición dentro del grupo).
      const previous = list_ref.value;
      const kept = previous.filter((x) => x.existing);
      const seen = {};
      list_ref.value = kept.concat(
        generated.map((raw) => {
          const group = raw.study_unit_key;
          seen[group] = (seen[group] || 0) + 1;
          const group_previous = previous.filter(
            (x) => !x.existing && x.study_unit_key === group
          );
          const prev = group_previous[seen[group] - 1];
          return {
            key: prev ? prev.key : `${field === "content" ? "t" : "c"}-${++uid}`,
            study_unit_key: group,
            [field]: raw[field],
            ...baseMarks(),
            deleted: prev ? prev.deleted : false,
            theme: prev ? prev.theme : "",
          };
        })
      );
    }
    function reconcile(data) {
      reconcileKeyed(units, data.units || [], (raw) => ({
        key: raw.key,
        name: raw.name,
        full_sentence: raw.full_sentence,
        ...baseMarks(),
      }));
      reconcileKeyed(study_units, data.study_units || [], (raw) => ({
        key: raw.key,
        unit_key: raw.unit_key || null,
        name: raw.name,
        full_sentence: raw.full_sentence,
        ...baseMarks(),
      }));
      reconcileGrouped(criteria, data.criteria || [], "full_sentence");
      reconcileGrouped(contents, data.contents || [], "content");
    }

    async function generateAll() {
      buildExisting();
      // La estructura curricular es una sola por asignatura: si ya existe
      // algo, no se pide una propuesta completa (mismo criterio que el
      // backend); el usuario regenera puntualmente cada ítem.
      if (units.value.length > 0 || study_units.value.length > 0) return;
      const data = await callBuilder(
        {
          mode: "all",
          egress_profile_matter: props.egress_profile_matter_id,
        },
        (event) => {
          if (event.type === "unit") pushGeneratedUnit(event.item);
          else if (event.type === "study_unit")
            pushGeneratedStudyUnit(event.item);
          else if (event.type === "criteria")
            pushGeneratedCriteria(event.item);
          else if (event.type === "content") pushGeneratedContent(event.item);
        }
      );
      reconcile(data);
    }

    // Lote de agregado (unit_tree / study_unit_tree): streamea los ítems
    // prefijando las claves de la IA con el lote y, al terminar, reemplaza
    // lo streameado por el resultado final validado (autoritativo para el
    // lote; mientras se generaba, `busy` bloqueó las marcas del usuario,
    // así que no hay nada que preservar). `studyUnitKeyOf` resuelve la
    // unit_key de cada RA generado (remapeada del lote o fijada al padre).
    async function runTreeBatch(payload, studyUnitKeyOf) {
      const batch = `b${++batch_seq}`;
      const remap = (key) => (key ? `${batch}:${key}` : null);
      const pushItem = (type, item) => {
        if (type === "unit")
          pushGeneratedUnit({ ...item, key: remap(item.key) }, { batch });
        else if (type === "study_unit")
          pushGeneratedStudyUnit(
            {
              ...item,
              key: remap(item.key),
              unit_key: studyUnitKeyOf(item, remap),
            },
            { batch }
          );
        else if (type === "criteria")
          pushGeneratedCriteria(
            { ...item, study_unit_key: remap(item.study_unit_key) },
            { batch }
          );
        else if (type === "content")
          pushGeneratedContent(
            { ...item, study_unit_key: remap(item.study_unit_key) },
            { batch }
          );
      };
      error.value = null;
      generating.value = true;
      try {
        const data = await callBuilder(payload, (event) =>
          pushItem(event.type, event.item || {})
        );
        units.value = units.value.filter((x) => x.batch !== batch);
        study_units.value = study_units.value.filter(
          (x) => x.batch !== batch
        );
        criteria.value = criteria.value.filter((x) => x.batch !== batch);
        contents.value = contents.value.filter((x) => x.batch !== batch);
        (data.units || []).forEach((raw) => pushItem("unit", raw));
        (data.study_units || []).forEach((raw) =>
          pushItem("study_unit", raw)
        );
        (data.criteria || []).forEach((raw) => pushItem("criteria", raw));
        (data.contents || []).forEach((raw) => pushItem("content", raw));
        return true;
      } catch (e) {
        error.value = e.message;
        return false;
      } finally {
        generating.value = false;
      }
    }

    // Agrega 1 unidad de competencia NUEVA con su subárbol completo (RAs +
    // indicadores + contenidos). A diferencia de la generación inicial,
    // funciona también cuando la asignatura ya tiene estructura.
    async function addUnitTree() {
      const added = await runTreeBatch(
        {
          mode: "unit_tree",
          egress_profile_matter: props.egress_profile_matter_id,
          theme: add_unit_theme.value,
          avoid_sentences: units.value
            .filter((x) => !x.deleted && x.full_sentence)
            .map((x) => x.full_sentence),
        },
        (item, remap) => remap(item.unit_key)
      );
      if (added) add_unit_theme.value = "";
    }

    // Agrega 1 RA NUEVO (con sus indicadores + contenidos) colgando de la
    // unidad de competencia dada, o directo de la asignatura (unit = null)
    // cuando la institución no usa unidades de competencia.
    async function addStudyUnitTree(unit) {
      const group_key = unit ? unit.key : "__none__";
      const added = await runTreeBatch(
        {
          mode: "study_unit_tree",
          egress_profile_matter: props.egress_profile_matter_id,
          theme: add_study_unit_themes.value[group_key] || "",
          unit_sentence: unit ? unit.full_sentence : "",
          avoid_sentences: study_units.value
            .filter((x) => !x.deleted && x.full_sentence)
            .map((x) => x.full_sentence),
        },
        () => (unit ? unit.key : null)
      );
      if (added) setAddStudyUnitTheme(group_key, "");
    }

    // Agregado de 1 ítem hoja (indicador/contenido) vía IA: mismo flujo que
    // un lote pero sin claves remapeadas (la clave local se asigna al
    // pushear). Se reutilizan los modos puntuales del backend ("criteria" /
    // "content"), que devuelven un único ítem.
    async function addSingleItem(payload, apply) {
      error.value = null;
      generating.value = true;
      try {
        const data = await callBuilder({
          egress_profile_matter: props.egress_profile_matter_id,
          ...payload,
        });
        apply(data);
        return true;
      } catch (e) {
        error.value = e.message;
        return false;
      } finally {
        generating.value = false;
      }
    }

    // Agrega 1 indicador de logro NUEVO al RA dado.
    async function addCriteria(study_unit) {
      const added = await addSingleItem(
        {
          mode: "criteria",
          theme: add_criteria_themes.value[study_unit.key] || "",
          study_unit_sentence: study_unit.full_sentence,
          unit_sentence: parentUnitSentence(study_unit),
          avoid_sentences: avoidSentences(criteriaOf(study_unit.key), null),
        },
        (data) => {
          const generated = (data.criteria || [])[0];
          if (generated)
            pushGeneratedCriteria({
              study_unit_key: study_unit.key,
              full_sentence: generated.full_sentence,
            });
        }
      );
      if (added) setAddCriteriaTheme(study_unit.key, "");
    }

    // Agrega 1 contenido NUEVO al RA dado.
    async function addContent(study_unit) {
      const added = await addSingleItem(
        {
          mode: "content",
          theme: add_content_themes.value[study_unit.key] || "",
          study_unit_sentence: study_unit.full_sentence,
          avoid_sentences: avoidSentences(
            contentsOf(study_unit.key),
            null,
            "content"
          ),
        },
        (data) => {
          const generated = (data.contents || [])[0];
          if (generated)
            pushGeneratedContent({
              study_unit_key: study_unit.key,
              content: generated.content,
            });
        }
      );
      if (added) setAddContentTheme(study_unit.key, "");
    }

    async function open() {
      visible.value = true;
      error.value = null;
      loading.value = true;
      units.value = [];
      study_units.value = [];
      criteria.value = [];
      contents.value = [];
      try {
        const ep_matter = await store.dispatch(
          names.FETCH_EGRESS_PROFILE_MATTER,
          props.egress_profile_matter_id
        );
        matter_id.value = ep_matter.matter;
        const matter_study_units = await store.dispatch(
          names.FETCH_STUDY_UNITS,
          matter_id.value
        );
        await Promise.all([
          store.dispatch(names.FETCH_COMPETENCE_UNITS, {
            matter__id: matter_id.value,
            force: true,
          }),
          store.dispatch(names.FETCH_EVALUATION_CRITERIAS, {
            matter_id: matter_id.value,
          }),
          // Los contenidos solo se pueden pedir por unidad de estudio.
          ...(show_contents.value ? matter_study_units : []).map(
            (study_unit) => store.dispatch(names.FETCH_CONTENTS, study_unit.id)
          ),
        ]);
        loading.value = false;
        generating.value = true;
        await generateAll();
      } catch (e) {
        error.value =
          e.message ||
          proxy.$t("reusable.aiCurricularBuilderModal.generateError");
      } finally {
        loading.value = false;
        generating.value = false;
      }
    }

    function close() {
      if (saving.value) return;
      visible.value = false;
      emit("closed");
    }

    // Quitar/Restaurar en cascada: eliminar una unidad marca también sus RA
    // (y estos sus indicadores/contenidos), igual que hará el backend al
    // borrar (FK CASCADE / delete() del modelo).
    function removeStudyUnit(study_unit) {
      study_unit.deleted = true;
      criteriaOf(study_unit.key).forEach((x) => (x.deleted = true));
      contentsOf(study_unit.key).forEach((x) => (x.deleted = true));
    }
    function restoreStudyUnit(study_unit) {
      study_unit.deleted = false;
      criteriaOf(study_unit.key).forEach((x) => (x.deleted = false));
      contentsOf(study_unit.key).forEach((x) => (x.deleted = false));
    }
    function removeUnit(unit) {
      unit.deleted = true;
      study_units.value
        .filter((x) => x.unit_key === unit.key)
        .forEach(removeStudyUnit);
    }
    function restoreUnit(unit) {
      unit.deleted = false;
      study_units.value
        .filter((x) => x.unit_key === unit.key)
        .forEach(restoreStudyUnit);
    }

    function avoidSentences(list, item, field = "full_sentence") {
      // Hermanas visibles de la sesión (el backend no puede conocer a qué
      // padre pertenece una propuesta aún no guardada, así que aquí se
      // mandan todas, existentes incluidas).
      return list
        .filter((x) => x !== item && !x.deleted && x[field])
        .map((x) => x[field]);
    }
    function parentUnitSentence(study_unit) {
      const unit = units.value.find((x) => x.key === study_unit.unit_key);
      return unit ? unit.full_sentence : "";
    }
    function parentStudyUnit(child) {
      return study_units.value.find((x) => x.key === child.study_unit_key);
    }

    async function regenerateItem(item, payload, apply) {
      item.regenerating = true;
      error.value = null;
      try {
        const data = await callBuilder({
          egress_profile_matter: props.egress_profile_matter_id,
          theme: item.theme,
          ...payload,
        });
        apply(data);
        item.deleted = false;
      } catch (e) {
        error.value = e.message;
      } finally {
        item.regenerating = false;
      }
    }
    function regenerateUnit(item) {
      return regenerateItem(
        item,
        {
          mode: "unit",
          avoid_sentences: avoidSentences(units.value, item),
        },
        (data) => {
          const generated = (data.units || [])[0];
          if (!generated) return;
          item.full_sentence = generated.full_sentence;
          if (generated.name) item.name = generated.name;
        }
      );
    }
    function regenerateStudyUnit(item) {
      return regenerateItem(
        item,
        {
          mode: "study_unit",
          unit_sentence: parentUnitSentence(item),
          avoid_sentences: avoidSentences(study_units.value, item),
        },
        (data) => {
          const generated = (data.study_units || [])[0];
          if (!generated) return;
          item.full_sentence = generated.full_sentence;
          if (generated.name) item.name = generated.name;
        }
      );
    }
    function regenerateCriteria(item) {
      const study_unit = parentStudyUnit(item);
      return regenerateItem(
        item,
        {
          mode: "criteria",
          study_unit_sentence: study_unit ? study_unit.full_sentence : "",
          unit_sentence: study_unit ? parentUnitSentence(study_unit) : "",
          avoid_sentences: avoidSentences(
            criteriaOf(item.study_unit_key),
            item
          ),
        },
        (data) => {
          const generated = (data.criteria || [])[0];
          if (generated) item.full_sentence = generated.full_sentence;
        }
      );
    }
    function regenerateContent(item) {
      const study_unit = parentStudyUnit(item);
      return regenerateItem(
        item,
        {
          mode: "content",
          study_unit_sentence: study_unit ? study_unit.full_sentence : "",
          avoid_sentences: avoidSentences(
            contentsOf(item.study_unit_key),
            item,
            "content"
          ),
        },
        (data) => {
          const generated = (data.contents || [])[0];
          if (generated) item.content = generated.content;
        }
      );
    }

    function sentenceFields(item) {
      // Oración única, modo "Insertar": action/verb derivados del texto y
      // el resto de las partes vacías (igual que los demás builders).
      const { action, verb } = deriveActionVerb(item.full_sentence, verbs.value);
      return {
        action,
        verb,
        content: "",
        condition: "",
        context: "",
        full_sentence: item.full_sentence,
      };
    }

    async function save() {
      saving.value = true;
      error.value = null;
      try {
        const needs_verb_derivation = units.value
          .concat(study_units.value, criteria.value)
          .some(
            (x) =>
              !x.deleted && (x.existing ? isSentenceChanged(x) : true)
          );
        if (needs_verb_derivation) await ensureTaxonomies();

        // Ids reales por clave (existentes de entrada, creados en el loop) y
        // padres a los que hay que redistribuir pesos al final.
        const unit_ids = {};
        const study_unit_ids = {};
        let matter_weighing_dirty = false;
        const dirty_unit_ids = new Set();
        const dirty_study_unit_ids = new Set();

        let unit_order = store.getters[names.COMPETENCE_UNITS]
          .filter((x) => x.matter == matter_id.value)
          .reduce((max, x) => Math.max(max, x.order || 0), 0);
        for (const unit of units.value) {
          if (unit.existing) {
            if (unit.deleted) {
              // Borra la unidad completa; el backend cascada sus RA.
              await store.dispatch(names.DELETE_STUDY_COMPETENCE, unit.unit_id);
              matter_weighing_dirty = true;
              continue;
            }
            if (isSentenceChanged(unit))
              await store.dispatch(names.PATCH_STUDY_COMPETENCE, {
                study_competence_id: unit.unit_id,
                item: { name: unit.name, ...sentenceFields(unit) },
              });
            unit_ids[unit.key] = unit.unit_id;
            continue;
          }
          if (unit.deleted) continue;
          unit_order += 1;
          const created = await store.dispatch(names.POST_STUDY_COMPETENCE, {
            name: unit.name,
            description: "",
            ...sentenceFields(unit),
            order: unit_order,
            weighing: 0,
            matter: matter_id.value,
            automatic_achievement_weighing: true,
            competence: null,
          });
          unit_ids[unit.key] = created.id;
          matter_weighing_dirty = true;
        }

        let study_unit_order = store.getters[names.STUDY_UNITS]
          .filter((x) => x.matter == matter_id.value)
          .reduce((max, x) => Math.max(max, x.order || 0), 0);
        for (const study_unit of study_units.value) {
          const parent_unit = study_unit.unit_key
            ? units.value.find((x) => x.key === study_unit.unit_key)
            : null;
          const parent_deleted = parent_unit != null && parent_unit.deleted;
          if (study_unit.existing) {
            if (study_unit.deleted) {
              // Si la unidad existente también se eliminó, el backend ya
              // cascadeó este RA con el DELETE de la unidad.
              if (!(parent_deleted && parent_unit.existing))
                await store.dispatch(
                  names.DELETE_STUDY_UNIT,
                  study_unit.study_unit_id
                );
              matter_weighing_dirty = true;
              continue;
            }
            if (isSentenceChanged(study_unit))
              await store.dispatch(names.PATCH_STUDY_UNIT, {
                study_unit_id: study_unit.study_unit_id,
                item: {
                  name: study_unit.name,
                  ...sentenceFields(study_unit),
                },
              });
            study_unit_ids[study_unit.key] = study_unit.study_unit_id;
            continue;
          }
          if (study_unit.deleted) continue;
          study_unit_order += 1;
          const temp_competence_unit = study_unit.unit_key
            ? unit_ids[study_unit.unit_key] || null
            : null;
          const created = await store.dispatch(names.POST_STUDY_UNIT, {
            name: study_unit.name,
            description: "",
            ...sentenceFields(study_unit),
            order: study_unit_order,
            weighing: 0,
            hours: 0,
            matter: matter_id.value,
            methodological_strategies: [],
            study_environments: [],
            automatic_achievement_weighing: true,
            temp_competence_unit,
          });
          study_unit_ids[study_unit.key] = created.id;
          matter_weighing_dirty = true;
          if (temp_competence_unit) dirty_unit_ids.add(temp_competence_unit);
        }

        const criteria_order = {};
        for (const criterion of criteria.value) {
          const study_unit_id = study_unit_ids[criterion.study_unit_key];
          if (criterion.existing) {
            if (criterion.deleted) {
              // Cascada backend si su RA existente también se eliminó.
              if (study_unit_id != null)
                await store.dispatch(
                  names.DELETE_EVALUATION_CRITERIA,
                  criterion.criteria_id
                );
              if (study_unit_id != null)
                dirty_study_unit_ids.add(study_unit_id);
              continue;
            }
            if (study_unit_id == null) continue;
            if (isSentenceChanged(criterion))
              await store.dispatch(names.PATCH_EVALUATION_CRITERIA, {
                evaluation_criterio_id: criterion.criteria_id,
                item: sentenceFields(criterion),
              });
            continue;
          }
          if (criterion.deleted || study_unit_id == null) continue;
          if (criteria_order[study_unit_id] == null)
            criteria_order[study_unit_id] = criteria.value.filter(
              (x) =>
                x.existing &&
                !x.deleted &&
                x.study_unit_key === criterion.study_unit_key
            ).length;
          criteria_order[study_unit_id] += 1;
          await store.dispatch(names.POST_EVALUATION_CRITERIA, {
            ...sentenceFields(criterion),
            order: criteria_order[study_unit_id],
            study_unit: study_unit_id,
            weighing: 0,
            competence: null,
            performance_indicator: null,
          });
          dirty_study_unit_ids.add(study_unit_id);
        }

        const content_order = {};
        for (const content of contents.value) {
          const study_unit_id = study_unit_ids[content.study_unit_key];
          if (content.existing) {
            if (content.deleted) {
              if (study_unit_id != null)
                await store.dispatch(names.DELETE_CONTENT, content.content_id);
              continue;
            }
            if (study_unit_id == null) continue;
            if (isContentChanged(content))
              await store.dispatch(names.PATCH_CONTENT, {
                content_id: content.content_id,
                item: { content: content.content },
              });
            continue;
          }
          if (content.deleted || study_unit_id == null) continue;
          if (content_order[study_unit_id] == null)
            content_order[study_unit_id] = contents.value.filter(
              (x) =>
                x.existing &&
                !x.deleted &&
                x.study_unit_key === content.study_unit_key
            ).length;
          content_order[study_unit_id] += 1;
          await store.dispatch(names.POST_CONTENT, {
            study_unit: study_unit_id,
            content: content.content,
            order: content_order[study_unit_id],
          });
        }

        // Redistribución de pesos equivalentes (mismos endpoints que usan
        // CompetenceUnitForm/StudyUnitForm/EvaluationCriteriaForm).
        if (matter_weighing_dirty)
          await restful.Get(
            `teaching/toggle_matter_automatic_achievement_weighing/?matter=${matter_id.value}`
          );
        for (const unit_id of dirty_unit_ids)
          await restful.Get(
            `teaching/toggle_temp_competence_unit_automatic_achievement_weighing/?temp_competence_unit=${unit_id}`
          );
        for (const study_unit_id of dirty_study_unit_ids)
          await restful.Get(
            `teaching/toggle_study_unit_automatic_achievement_weighing/?study_unit=${study_unit_id}`
          );

        // Los toggles recalculan pesos server-side: se refresca el estado
        // local para que MatterProgram muestre los valores reales.
        await Promise.all([
          store.dispatch(names.FETCH_COMPETENCE_UNITS, {
            matter__id: matter_id.value,
            force: true,
          }),
          store.dispatch(names.FETCH_STUDY_UNITS, matter_id.value),
          store.dispatch(names.FETCH_EVALUATION_CRITERIAS, {
            matter_id: matter_id.value,
          }),
        ]);

        toast(proxy.$t("reusable.aiCurricularBuilderModal.savedToast"));
        emit("saved");
        visible.value = false;
        emit("closed");
      } catch (e) {
        error.value =
          e.message ||
          proxy.$t("reusable.aiCurricularBuilderModal.saveError");
      } finally {
        saving.value = false;
      }
    }

    return {
      visible,
      loading,
      generating,
      saving,
      error,
      units,
      study_units,
      criteria,
      contents,
      busy,
      hasPendingItems,
      unit_groups,
      can_add_unit,
      add_unit_theme,
      add_study_unit_themes,
      setAddStudyUnitTheme,
      unit_label,
      study_unit_label,
      study_units_label,
      criterion_label,
      criteria_title,
      content_label,
      contents_title,
      show_contents,
      no_contents_suffix,
      add_criteria_themes,
      setAddCriteriaTheme,
      add_content_themes,
      setAddContentTheme,
      isCollapsed,
      toggleCollapsed,
      criteriaOf,
      contentsOf,
      addUnitTree,
      addStudyUnitTree,
      addCriteria,
      addContent,
      open,
      close,
      removeUnit,
      restoreUnit,
      removeStudyUnit,
      restoreStudyUnit,
      regenerateUnit,
      regenerateStudyUnit,
      regenerateCriteria,
      regenerateContent,
      save,
    };
  },
};
</script>

<style scoped src="./ai-builder/ai-builder.css"></style>

<style scoped>
.ai-builder-nested {
  margin-left: 1.5rem;
}
</style>
